import type { ForecastReport } from '@finapp/types';
import type React from 'react';
import { useEffect, useState } from 'react';
import { useAuth } from '../contexts/AuthContext';
import { forecastApi, type UpsertFiscalYearBalanceInput } from '../services/forecastApi';

interface OpeningBalanceFormProps {
  year: number;
  report: ForecastReport;
  /** Whether no opening balance has been recorded yet for `year` - see Forecast.tsx's
   *  isOpeningBalanceUnset. Only used to pick the form's heading ("Set" vs "Edit"). */
  isUnset: boolean;
  /** Called after a successful save, so the caller can refetch the forecast. */
  onSaved: () => void;
}

/**
 * Admin-only form for setting/editing a fiscal year's opening balance and optional
 * reconciliation fields (current_balance_override / current_balance_note), via
 * PUT /api/v1/fiscal-year-balances/:year. The endpoint itself upserts and is already
 * restricted to admin/superadmin server-side (fiscal-year-balance.controller.ts) - this
 * component is the client-side gate: non-admins never see the edit affordance at all,
 * matching the role-check convention used for the unlock/delete actions in BudgetPage.tsx.
 */
const OpeningBalanceForm: React.FC<OpeningBalanceFormProps> = ({
  year,
  report,
  isUnset,
  onSaved,
}) => {
  const { user } = useAuth();

  const [openingBalanceInput, setOpeningBalanceInput] = useState('');
  const [overrideInput, setOverrideInput] = useState('');
  const [noteInput, setNoteInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState(false);

  // Pre-fill from the currently loaded report, but only once that report actually
  // belongs to this `year` - while a year change is in flight, useForecastData keeps
  // the *previous* year's report around until the new one arrives, and we don't want
  // to briefly fill the form with the wrong year's figures in the meantime.
  useEffect(() => {
    if (report.year !== year) {
      return;
    }
    setOpeningBalanceInput(String(report.opening_balance));
    setOverrideInput(
      report.current_balance_override === null ? '' : String(report.current_balance_override)
    );
    setNoteInput(report.current_balance_note ?? '');
    setError(null);
    setSuccess(false);
  }, [year, report]);

  if (!user || !['admin', 'superadmin'].includes(user.role)) {
    return null;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(false);

    const trimmedOpening = openingBalanceInput.trim();
    if (trimmedOpening === '' || Number.isNaN(Number(trimmedOpening))) {
      setError('Opening balance is required and must be a number.');
      return;
    }

    const payload: UpsertFiscalYearBalanceInput = {
      opening_balance: Number(trimmedOpening),
    };

    const trimmedOverride = overrideInput.trim();
    if (trimmedOverride !== '') {
      if (Number.isNaN(Number(trimmedOverride))) {
        setError('Current balance override must be a number.');
        return;
      }
      payload.current_balance_override = Number(trimmedOverride);
    }

    const trimmedNote = noteInput.trim();
    if (trimmedNote !== '') {
      payload.current_balance_note = trimmedNote;
    }

    try {
      setSaving(true);
      await forecastApi.upsertFiscalYearBalance(year, payload);
      setSuccess(true);
      onSaved();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save opening balance.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="mt-4 pt-4 border-t border-gray-100" data-testid="opening-balance-form">
      <h4 className="text-sm font-medium text-gray-900 mb-2">
        {isUnset ? `Set Opening Balance for ${year}` : `Edit Opening Balance for ${year}`}
      </h4>

      {error && (
        <div
          className="mb-3 p-2 bg-red-50 border border-red-200 rounded-md text-sm text-red-700"
          data-testid="opening-balance-error"
        >
          {error}
        </div>
      )}

      {success && !error && (
        <div
          className="mb-3 p-2 bg-green-50 border border-green-200 rounded-md text-sm text-green-700"
          data-testid="opening-balance-success"
        >
          Opening balance saved.
        </div>
      )}

      {/* noValidate: we show our own inline validation messages below instead of the
          browser's native constraint-validation tooltips (which would also silently
          swallow a submit attempt on an empty `required` field before our handler runs). */}
      <form onSubmit={handleSubmit} className="space-y-3" noValidate>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
          <div>
            <label
              htmlFor="opening-balance-input"
              className="block text-xs font-medium text-gray-700 mb-1"
            >
              Opening balance (€)
            </label>
            <input
              id="opening-balance-input"
              type="text"
              inputMode="decimal"
              required
              value={openingBalanceInput}
              onChange={(e) => setOpeningBalanceInput(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              data-testid="opening-balance-input"
            />
          </div>

          <div>
            <label
              htmlFor="current-balance-override-input"
              className="block text-xs font-medium text-gray-700 mb-1"
            >
              Current balance override (€, optional)
            </label>
            <input
              id="current-balance-override-input"
              type="text"
              inputMode="decimal"
              value={overrideInput}
              onChange={(e) => setOverrideInput(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              data-testid="current-balance-override-input"
            />
          </div>

          <div>
            <label
              htmlFor="current-balance-note-input"
              className="block text-xs font-medium text-gray-700 mb-1"
            >
              Note (optional)
            </label>
            <input
              id="current-balance-note-input"
              type="text"
              value={noteInput}
              onChange={(e) => setNoteInput(e.target.value)}
              placeholder="e.g. reconciled with bank statement"
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
              data-testid="current-balance-note-input"
            />
          </div>
        </div>

        <button
          type="submit"
          disabled={saving}
          className="bg-indigo-600 text-white px-4 py-2 rounded-md hover:bg-indigo-700 disabled:opacity-50 text-sm font-medium"
          data-testid="opening-balance-submit"
        >
          {saving ? 'Saving...' : 'Save Opening Balance'}
        </button>
      </form>
    </div>
  );
};

export default OpeningBalanceForm;
